import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, DatePicker, Empty, Input, Segmented, Select, Space, Table, Tag, Typography } from 'antd';
import { DownloadOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import './AdminMailJournal.css';
import ManualReminderDialog from './ManualReminderDialog';

const kinds: Record<string, string> = {
  test_assigned: 'Назначение теста', retake_assigned: 'Пересдача', reminder: 'Напоминание', welcome: 'Приветственное письмо',
};
const statuses: Record<string, { label: string; color?: string }> = {
  pending: { label: 'Запланировано', color: 'blue' }, retry: { label: 'Повторная попытка', color: 'gold' },
  sending: { label: 'Отправляется', color: 'processing' }, sent: { label: 'Передано почтовому серверу', color: 'green' },
  failed: { label: 'Ошибка отправки', color: 'red' }, uncertain: { label: 'Требует проверки', color: 'orange' },
  cancelled: { label: 'Отменено' }, skipped: { label: 'Будет пропущено' },
};
interface MailRow {
  key: string; user_id: number | null; user_name: string | null; email: string | null;
  test_id: number | null; test_title: string | null; kind: string; status: string;
  event_at: string; scheduled_at: string; sent_at: string | null; due_at: string | null;
  attempts: number; error_code: string | null; skip_reason?: string | null;
  is_manual?: boolean;
}
interface Journal {
  items: MailRow[]; total: number;
  summary: { recipients: number; sent: number; scheduled: number; problems: number };
  enabled: boolean; worker: { state: string; last_successful_poll?: string };
}
const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
});
const showDate = (value: string | null) => value ? dateFormatter.format(new Date(value)) : '—';

export default function AdminMailJournal({ active }: { active: boolean }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<Journal | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportError, setExportError] = useState('');
  const [exporting, setExporting] = useState(false);
  const exportBusy = useRef(false);
  const [revision, setRevision] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);
  const view = ['all', 'scheduled', 'sent', 'problems'].includes(params.get('view') || '') ? params.get('view')! : 'sent';
  const kind = kinds[params.get('kind') || ''] ? params.get('kind')! : undefined;
  const dateFrom = params.get('date_from') || undefined;
  const dateTo = params.get('date_to') || undefined;
  const search = params.get('search') || '';
  const batchId = params.get('batch_id') || undefined;
  const [searchInput, setSearchInput] = useState(search);
  const page = Math.max(1, Number(params.get('page')) || 1);
  const pageSize = [25, 50, 100].includes(Number(params.get('page_size'))) ? Number(params.get('page_size')) : 25;
  const requestParams = useMemo(() => ({
    view, kind, date_from: dateFrom, date_to: dateTo, search: search || undefined, page, page_size: pageSize, batch_id: batchId,
  }), [view, kind, dateFrom, dateTo, search, page, pageSize, batchId]);

  useEffect(() => {
    if (!active || !batchId || !data?.summary.scheduled) return;
    const timer = window.setTimeout(() => setRevision(value => value + 1), 5000);
    return () => window.clearTimeout(timer);
  }, [active, batchId, data]);

  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    setLoading(true);
    setData(null);
    setError('');
    setExportError('');
    api.get<Journal>('/admin/mail-notifications', { params: requestParams, signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) setData(response.data); })
      .catch(reason => {
        if (!controller.signal.aborted) setError(getApiErrorMessage(reason, 'Не удалось загрузить журнал рассылки'));
      }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [active, requestParams, revision]);

  const changeFilters = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    next.delete('page');
    Object.entries(changes).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
    setParams(next);
  };
  const download = async () => {
    if (exportBusy.current) return;
    exportBusy.current = true;
    setExporting(true);
    setExportError('');
    let url: string | undefined;
    try {
      const response = await api.get('/admin/mail-notifications', {
        params: { ...requestParams, format: 'csv' }, responseType: 'blob',
      });
      url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'FEBEST — журнал рассылки.csv';
      anchor.click();
    } catch {
      setExportError('Не удалось скачать список. Попробуйте ещё раз.');
    } finally {
      if (url) URL.revokeObjectURL(url);
      setExporting(false);
      exportBusy.current = false;
    }
  };

  const columns = [
    { title: 'Сотрудник / получатель', key: 'recipient', width: 270, render: (_: unknown, row: MailRow) => (
      <div className="mail-journal-recipient"><strong>{row.user_name || 'Учётная запись удалена'}</strong>
        {row.email ? <a href={`mailto:${row.email}`}>{row.email}</a> : <span>Адрес недоступен</span>}
      </div>
    ) },
    { title: 'Письмо и тест', key: 'test', width: 330, render: (_: unknown, row: MailRow) => (
      <div className="mail-journal-test"><span><Tag>{kinds[row.kind] || row.kind}</Tag>{row.is_manual && <Tag color="purple">Вручную</Tag>}</span>
        <span>{row.kind === 'welcome' ? 'Запуск уведомлений платформы' : row.test_title || 'Назначение удалено'}</span>
        {row.due_at && <small>Срок прохождения: {showDate(row.due_at)} МСК</small>}
      </div>
    ) },
    { title: 'Статус', dataIndex: 'status', key: 'status', width: 250, render: (value: string, row: MailRow) => (
      <Space direction="vertical" size={4}>
        <Tag color={statuses[value]?.color}>{statuses[value]?.label || value}</Tag>
        {value === 'skipped' && <Typography.Text type="secondary">{row.skip_reason || 'Письмо больше не актуально'}</Typography.Text>}
        {row.error_code && <Typography.Text type="secondary">{row.error_code === 'manual_reminder_replaced' ? 'Заменено напоминанием вручную' : `Код: ${row.error_code}`}</Typography.Text>}
      </Space>
    ) },
    { title: 'Дата и время, МСК', key: 'date', width: 185, render: (_: unknown, row: MailRow) => (
      <div className="mail-journal-date"><strong>{showDate(row.event_at)}</strong>
        <span>{row.status === 'sent' ? 'Отправлено' : 'По плану'}</span>
      </div>
    ) },
  ];
  const working = data?.enabled && data.worker.state === 'ready';

  return <div className="mail-journal">
    <div className="mail-journal-heading">
      <div><Typography.Title level={3}>Почтовая рассылка</Typography.Title>
        <Typography.Text type="secondary">История отправки и запланированные уведомления сотрудникам</Typography.Text>
      </div>
      <Space wrap>
        {data && <Tag color={working ? 'green' : 'orange'}>{working ? 'Рассылка работает' : data.enabled ? 'Обработчик недоступен' : 'Рассылка выключена'}</Tag>}
        <Button type="primary" icon={<SendOutlined />} onClick={() => setManualOpen(true)} disabled={!working}>Отправить напоминания сейчас</Button>
        <Button icon={<ReloadOutlined />} onClick={() => setRevision(value => value + 1)} loading={loading}>Обновить</Button>
        <Button icon={<DownloadOutlined />} onClick={download} loading={exporting} disabled={!data?.total || loading}>Скачать CSV</Button>
      </Space>
    </div>
    <Alert type="info" showIcon message="Все даты указаны по московскому времени"
      description="«Передано почтовому серверу» подтверждает приём письма сервером отправки. Доставка во входящие и прочтение здесь не отслеживаются. Период отбирает отправленные письма по дате отправки, остальные — по запланированной дате." />
    <Card>
      <div className="mail-journal-filters">
        {batchId && <Tag color="purple" closable onClose={() => changeFilters({ batch_id: undefined })}>Ручная отправка</Tag>}
        <Segmented aria-label="Показать письма" value={view} onChange={value => changeFilters({ view: String(value) })}
          options={[{ value: 'sent', label: 'Отправленные' }, { value: 'scheduled', label: 'Запланированные' },
            { value: 'problems', label: 'Проблемы' }, { value: 'all', label: 'Все' }]} />
        <div className="mail-journal-filter-fields">
          <Input.Search aria-label="Поиск по сотруднику или тесту" placeholder="Сотрудник, email или название теста" allowClear
            value={searchInput} onChange={event => {
              setSearchInput(event.target.value);
              if (!event.target.value) changeFilters({ search: undefined });
            }} onSearch={value => changeFilters({ search: value.trim() || undefined })} enterButton />
          <Select aria-label="Тип письма" placeholder="Все типы писем" allowClear value={kind}
            options={Object.entries(kinds).map(([value, label]) => ({ value, label }))}
            onChange={value => changeFilters({ kind: value })} />
          <DatePicker.RangePicker aria-label="Период рассылки" format="DD.MM.YYYY"
            placeholder={['Дата с', 'Дата по']} value={dateFrom && dateTo ? [dayjs(dateFrom), dayjs(dateTo)] : null}
            onChange={value => changeFilters({ date_from: value?.[0]?.format('YYYY-MM-DD'), date_to: value?.[1]?.format('YYYY-MM-DD') })} />
          <Button onClick={() => setParams({ view })}>Сбросить фильтры</Button>
        </div>
      </div>
      {data && <div className="mail-journal-summary" aria-label="Итоги по выбранным фильтрам">
        <span>Писем: <strong>{data.total}</strong></span><span>Получателей: <strong>{data.summary.recipients}</strong></span>
        <span>Отправлено: <strong>{data.summary.sent}</strong></span><span>Запланировано: <strong>{data.summary.scheduled}</strong></span>
        <span>Проблем: <strong>{data.summary.problems}</strong></span>
      </div>}
      {error && <Alert type="error" showIcon message={error} action={<Button onClick={() => setRevision(value => value + 1)}>Повторить</Button>} />}
      {exportError && <Alert type="error" showIcon message={exportError} />}
      <Table<MailRow> rowKey="key" columns={columns} dataSource={data?.items || []} loading={loading} scroll={{ x: 1035 }}
        locale={{ emptyText: error ? 'Журнал не загружен' : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Писем по выбранным фильтрам нет" /> }}
        pagination={{ current: page, pageSize, total: data?.total || 0, showSizeChanger: true, pageSizeOptions: [25, 50, 100],
          showTotal: total => `Всего писем: ${total}`, onChange: (nextPage, nextSize) => changeFilters({ page: String(nextPage), page_size: String(nextSize) }) }} />
    </Card>
    <ManualReminderDialog open={manualOpen} onClose={() => setManualOpen(false)} onQueued={id => {
      setParams({ view: 'all', batch_id: id });
      setRevision(value => value + 1);
    }} />
  </div>;
}
